/**
 * Customer-specific minimum order value.
 *
 * A currency select + amount input, matching the sibling Settings-tab cards
 * (Price Visibility Override, Delivery Days & Cutoff Times). Bound to the
 * `customerLevelMovCurrency` / `customerLevelMovAmount` form fields (amount is
 * held as a major-unit string, e.g. "1000"); the save handler converts to minor
 * units — scaled by the currency's ISO-4217 minor-unit decimals — and fires
 * updateSupplierBuyerMov, or clears the override when both fields are emptied.
 *
 * This component is presentation only: it never fires a mutation (not even the
 * "Clear override" button, which just empties the fields so the normal Save
 * flow performs the both-empty → removeSupplierBuyerMov write).
 */

import type { CreateChatFormData } from '@choco-admin/types';
import {
  buildCurrencyOptions,
  getCurrencyDecimalPlaces,
  getCurrencySymbol,
} from '@choco-admin/utils';
import { Button, Card, CardBody } from '@heroui/react';
import React, { useMemo } from 'react';
import { Controller, useFormContext } from 'react-hook-form';

import { Input, ChocoSelect } from '../../../../components/FormComponents';

// The full ISO 4217 list, same as the distributor's currency: the backend
// accepts any code, so a shorter list renders stored values (e.g. TRY) empty.
const CURRENCY_OPTIONS = buildCurrencyOptions();

// Decimals-aware amount placeholder, e.g. "250.00" (2-decimal), "250" (JPY).
const amountPlaceholderFor = (currency: string): string => {
  const decimals = getCurrencyDecimalPlaces(currency);
  return decimals > 0 ? `250.${'0'.repeat(decimals)}` : '250';
};

interface CustomerLevelMovSectionProps {
  disabled?: boolean;
}

export const CustomerLevelMovSection: React.FC<
  CustomerLevelMovSectionProps
> = ({ disabled = false }) => {
  const { control, watch, setValue, formState } =
    useFormContext<CreateChatFormData>();

  const amountValue = watch('customerLevelMovAmount') ?? '';
  const currencyValue = watch('customerLevelMovCurrency') ?? '';

  const amountSet = amountValue.trim() !== '';
  const currencySet = currencyValue.trim() !== '';

  // Inline pair-validation hint. Mirrors the save-time pair rule (amount and
  // currency must be set together) as a field-level message; it does NOT change
  // the save-time abort logic, which still toasts and keeps the fields dirty.
  const currencyError =
    amountSet && !currencySet
      ? 'Currency is required when an amount is set'
      : undefined;
  const amountError =
    currencySet && !amountSet
      ? 'Amount is required when a currency is set'
      : undefined;

  // An override was loaded from the server when the RHF baseline carries a
  // value — that is when an explicit "Clear override" affordance is meaningful.
  const { defaultValues } = formState;
  const hasLoadedOverride = Boolean(
    (defaultValues?.customerLevelMovAmount ?? '').trim() ||
    (defaultValues?.customerLevelMovCurrency ?? '').trim()
  );
  const bothEmpty = !amountSet && !currencySet;
  const showClearButton = hasLoadedOverride && !bothEmpty && !disabled;
  const showClearedHelper = hasLoadedOverride && bothEmpty;

  const handleClearOverride = () => {
    // Empty both fields only — the existing both-empty save path fires
    // removeSupplierBuyerMov, so we deliberately do NOT run a mutation here.
    setValue('customerLevelMovAmount', '', {
      shouldDirty: true,
      shouldValidate: true,
    });
    setValue('customerLevelMovCurrency', null, {
      shouldDirty: true,
      shouldValidate: true,
    });
  };

  const selectedSymbol = currencySet
    ? getCurrencySymbol(currencyValue)
    : undefined;

  // The backend stores the currency verbatim, so a value outside the ISO list
  // is still offered rather than leaving the select blank.
  const currencySelectOptions = useMemo(
    () =>
      currencySet &&
      !CURRENCY_OPTIONS.some((option) => option.value === currencyValue)
        ? [{ value: currencyValue, label: currencyValue }, ...CURRENCY_OPTIONS]
        : CURRENCY_OPTIONS,
    [currencySet, currencyValue]
  );

  return (
    <div className="space-y-4" data-testid="CustomerLevelMovSection">
      <Card>
        <CardBody className="space-y-4">
          <div className="flex items-start justify-between gap-4">
            <div className="space-y-2">
              <h4 className="font-medium text-foreground">
                Customer-specific minimum order value
              </h4>
              <p className="text-sm text-default-600">
                Overrides the distributor&apos;s default minimum order value for
                this customer only. Leave empty to use the distributor&apos;s
                default.
              </p>
            </div>
            {showClearButton && (
              <Button
                type="button"
                size="sm"
                variant="flat"
                color="danger"
                className="shrink-0"
                onPress={handleClearOverride}
              >
                Clear override
              </Button>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Controller
              name="customerLevelMovCurrency"
              control={control}
              render={({ field }) => (
                <ChocoSelect
                  label="Currency"
                  options={currencySelectOptions}
                  value={field.value ?? undefined}
                  onSelectionChange={(value) => field.onChange(value)}
                  placeholder="Select currency"
                  aria-label="Currency"
                  isDisabled={disabled}
                  errorMessage={currencyError}
                />
              )}
            />
            <Controller
              name="customerLevelMovAmount"
              control={control}
              render={({ field }) => (
                <Input
                  id="customerLevelMovAmount"
                  label="Amount"
                  value={field.value ?? ''}
                  onChange={(value) =>
                    // Keep digits and a single decimal point; empty clears MOV.
                    field.onChange(value.replace(/[^0-9.]/g, ''))
                  }
                  placeholder={amountPlaceholderFor(currencyValue)}
                  startContent={
                    selectedSymbol ? (
                      <span className="text-sm text-default-500">
                        {selectedSymbol}
                      </span>
                    ) : undefined
                  }
                  type="text"
                  disabled={disabled}
                  isInvalid={Boolean(amountError)}
                  errorMessage={amountError}
                />
              )}
            />
          </div>

          {showClearedHelper && (
            <p className="text-xs text-default-500">
              The distributor&apos;s default will apply after saving.
            </p>
          )}
        </CardBody>
      </Card>
    </div>
  );
};
